import { PageTitle } from "../../../components/Headers";
import { PageBody } from "../../../components/PageBody";
import { Helmet } from "react-helmet-async";
import { ArticleParagraph } from "../../../components/Paragraphs";
import { getSEOForPath } from "../../../config/seo";
import { useLocation } from "react-router-dom";
import { useState, useMemo } from "react";
import { BoardGrid } from "../../../components/games/snakesAndLadders/BoardGrid";
import { BoardConfigDisplay } from "../../../components/games/snakesAndLadders/BoardConfigDisplay";
import { GameControls } from "../../../components/games/snakesAndLadders/GameControls";
import { getDefaultConfig } from "../../../components/games/snakesAndLadders/boardConfig";
import { simulateGame, simulateMultipleGames } from "../../../components/games/snakesAndLadders/simulation";
import { SingleGameResults, MultiGameResults } from "../../../components/games/snakesAndLadders/SimulationResults";
import { BoardEditor } from "../../../components/games/snakesAndLadders/BoardEditor";
import { SimulationConfig } from "../../../components/games/snakesAndLadders/SimulationConfig";

// Tab component for switching between views
const Tab = ({ label, active, onClick }) => (
    <button
        onClick={onClick}
        className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
            active
                ? "bg-white text-badger-purple border-t border-l border-r border-gray-200"
                : "bg-gray-100 text-gray-600 hover:text-gray-900 hover:bg-gray-200"
        }`}
    >
        {label}
    </button>
);

// Board size options
const BOARD_SIZES = [
    { label: '5×5 (Quick)', rows: 5, cols: 5 },
    { label: '8×8 (Medium)', rows: 8, cols: 8 },
    { label: '10×10 (Classic)', rows: 10, cols: 10 },
];

// Dice configuration options
const DICE_SIDES_OPTIONS = [4, 6, 8, 10, 12, 20];
const DICE_COUNT_OPTIONS = [1, 2];

export const SnakesAndLadders = () => {
    const location = useLocation();
    const seo = getSEOForPath(location.pathname);
    const [activeTab, setActiveTab] = useState("play");
    const [boardSize, setBoardSize] = useState(BOARD_SIZES[2]); // Default to 10x10

    // Get default board config based on size
    const defaultConfig = useMemo(() => getDefaultConfig(boardSize.rows), [boardSize.rows]);

    // Custom snakes and ladders (null means use default)
    const [customSnakes, setCustomSnakes] = useState(null);
    const [customLadders, setCustomLadders] = useState(null);

    // Effective board config (use custom if set, otherwise default)
    const boardConfig = useMemo(() => ({
        ...defaultConfig,
        snakes: customSnakes !== null ? customSnakes : defaultConfig.snakes,
        ladders: customLadders !== null ? customLadders : defaultConfig.ladders
    }), [defaultConfig, customSnakes, customLadders]);

    // Game state
    const [position, setPosition] = useState(1);
    const [lastRollResult, setLastRollResult] = useState(null);  // { dice: [], total: }
    const [moveNarrative, setMoveNarrative] = useState(null);
    const [isWon, setIsWon] = useState(false);

    const maxPosition = boardSize.rows * boardSize.cols;

    // Simulation state
    const [singleSimResult, setSingleSimResult] = useState(null);
    const [multiSimResult, setMultiSimResult] = useState(null);
    const [isSimulating, setIsSimulating] = useState(false);
    const [startPosition, setStartPosition] = useState(1);

    // Dice configuration
    const [diceCount, setDiceCount] = useState(1);
    const [diceSides, setDiceSides] = useState(6);
    const [showDiceConfig, setShowDiceConfig] = useState(false);
    const [animationSpeed, setAnimationSpeed] = useState('normal');  // 'fast', 'normal', 'slow'

    const diceConfig = useMemo(() => ({ count: diceCount, sides: diceSides }), [diceCount, diceSides]);

    // Reset game when board size changes
    const handleBoardSizeChange = (index) => {
        setBoardSize(BOARD_SIZES[index]);
        setPosition(1);
        setLastRollResult(null);
        setMoveNarrative(null);
        setIsWon(false);
        setSingleSimResult(null);
        setMultiSimResult(null);
        setStartPosition(1);
        setDiceCount(1);
        setDiceSides(6);
        setCustomSnakes(null);
        setCustomLadders(null);
    };

    // Handle board config changes from editor
    const handleConfigChange = (newSnakes, newLadders) => {
        setCustomSnakes(newSnakes);
        setCustomLadders(newLadders);
        // Reset game state when config changes
        setPosition(1);
        setLastRollResult(null);
        setMoveNarrative(null);
        setIsWon(false);
        setSingleSimResult(null);
        setMultiSimResult(null);
    };

    // Reset to default config
    const handleResetConfig = () => {
        setCustomSnakes(null);
        setCustomLadders(null);
        setPosition(1);
        setLastRollResult(null);
        setMoveNarrative(null);
        setIsWon(false);
    };

    // Handle position change from manual input
    const handlePositionChange = (newPos) => {
        setPosition(newPos);
        setIsWon(newPos >= maxPosition);
        setMoveNarrative(null);
    };

    // Handle game reset
    const handleReset = () => {
        setPosition(1);
        setLastRollResult(null);
        setMoveNarrative(null);
        setIsWon(false);
    };

    // Handle dice roll
    const handleRoll = (result) => {
        if (isWon) return;

        const roll = result.total;
        setLastRollResult(result);

        const startPos = position;
        let diceEnd = startPos + roll;

        // Check for win (no bounceback - reaching or exceeding max wins)
        if (diceEnd >= maxPosition) {
            setPosition(maxPosition);
            setIsWon(true);
            setMoveNarrative(`Rolled a ${roll}, moved from ${startPos} to ${maxPosition}. YOU WIN!`);
            return;
        }

        // Check for snake or ladder
        let finalEnd = diceEnd;
        let hitSnake = null;
        let hitLadder = null;

        // Check snakes
        for (const snake of boardConfig.snakes) {
            if (snake.start === diceEnd) {
                hitSnake = snake;
                finalEnd = snake.end;
                break;
            }
        }

        // Check ladders (only if didn't hit snake)
        if (!hitSnake) {
            for (const ladder of boardConfig.ladders) {
                if (ladder.start === diceEnd) {
                    hitLadder = ladder;
                    finalEnd = ladder.end;
                    break;
                }
            }
        }

        // Update position
        setPosition(finalEnd);

        // Generate narrative
        let narrative = `Rolled a ${roll}, moved from ${startPos} to ${diceEnd}`;
        if (hitSnake) {
            narrative += `, that's a snake! Back to ${finalEnd}`;
        } else if (hitLadder) {
            narrative += `, nice ladder! Climbed to ${finalEnd}`;
        }
        setMoveNarrative(narrative);
    };

    // Run single game simulation
    const handleSingleSimulation = () => {
        setIsSimulating(true);
        // Use setTimeout to allow UI to update
        setTimeout(() => {
            const result = simulateGame(boardConfig, diceConfig, startPosition);
            setSingleSimResult(result);
            setIsSimulating(false);
        }, 50);
    };

    // Run multi-game simulation
    const handleMultiSimulation = (numGames) => {
        setIsSimulating(true);
        // Use setTimeout to allow UI to update
        setTimeout(() => {
            const result = simulateMultipleGames(numGames, boardConfig, diceConfig, startPosition);
            setMultiSimResult(result);
            setIsSimulating(false);
        }, 50);
    };

    return (
        <PageBody>
            <Helmet>
                <title>{seo.title}</title>
                <meta name="description" content={seo.description} />
                {seo.keywords && <meta name="keywords" content={seo.keywords} />}
            </Helmet>

            <PageTitle title="Snakes and Ladders" />

            <ArticleParagraph
                lines={[
                    "Snakes and Ladders is a classic board game that often feels like it takes forever. But how long does a typical game actually take? And what if you started halfway through the board?",
                    "This simulation tool lets you play the game interactively, run single game simulations with detailed move tracking, or simulate thousands of games to analyse patterns and probabilities."
                ]}
            />

            {/* Tab Navigation */}
            <div className="flex gap-1 mt-6 mb-0">
                <Tab
                    label="Play Game"
                    active={activeTab === "play"}
                    onClick={() => setActiveTab("play")}
                />
                <Tab
                    label="Single Simulation"
                    active={activeTab === "single"}
                    onClick={() => setActiveTab("single")}
                />
                <Tab
                    label="Multi-Simulation"
                    active={activeTab === "multi"}
                    onClick={() => setActiveTab("multi")}
                />
            </div>

            {/* Tab Content */}
            <div className="bg-white border border-gray-200 rounded-b-lg rounded-tr-lg p-6">
                {activeTab === "play" && (
                    <div>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
                            <h3 className="text-lg font-semibold text-gray-900">Play Game</h3>
                            <div className="flex items-center gap-2">
                                <label htmlFor="board-size" className="text-sm text-gray-600">Board size:</label>
                                <select
                                    id="board-size"
                                    value={BOARD_SIZES.findIndex(s => s.rows === boardSize.rows)}
                                    onChange={(e) => handleBoardSizeChange(e.target.value)}
                                    className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-badger-purple"
                                >
                                    {BOARD_SIZES.map((size, index) => (
                                        <option key={size.label} value={index}>{size.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Board */}
                            <div className="lg:col-span-2 flex flex-col items-center">
                                <BoardGrid
                                    rows={boardSize.rows}
                                    cols={boardSize.cols}
                                    maxPixels={500}
                                    snakes={boardConfig.snakes}
                                    ladders={boardConfig.ladders}
                                    counterPosition={position}
                                />
                                <BoardConfigDisplay config={boardConfig} />
                            </div>

                            {/* Controls */}
                            <div className="lg:col-span-1">
                                <GameControls
                                    position={position}
                                    maxPosition={maxPosition}
                                    onPositionChange={handlePositionChange}
                                    onRoll={handleRoll}
                                    lastRollResult={lastRollResult}
                                    isWon={isWon}
                                    onReset={handleReset}
                                    moveNarrative={moveNarrative}
                                    diceConfig={diceConfig}
                                    animationSpeed={animationSpeed}
                                />

                                {/* Dice Configuration */}
                                <div className="mt-4 border-t border-gray-200 pt-4">
                                    <button
                                        onClick={() => setShowDiceConfig(!showDiceConfig)}
                                        className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                                    >
                                        <span>{showDiceConfig ? '−' : '+'}</span>
                                        <span>Dice Settings</span>
                                        {(diceCount !== 1 || diceSides !== 6) && (
                                            <span className="text-xs bg-badger-purple text-white px-1.5 py-0.5 rounded ml-1">
                                                {diceCount}d{diceSides}
                                            </span>
                                        )}
                                    </button>
                                    {showDiceConfig && (
                                        <div className="mt-3 bg-gray-50 p-3 rounded-lg">
                                            <div className="grid grid-cols-3 gap-2">
                                                <div>
                                                    <label className="text-xs text-gray-500 block mb-1">Count</label>
                                                    <select
                                                        value={diceCount}
                                                        onChange={(e) => setDiceCount(parseInt(e.target.value))}
                                                        className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-badger-purple"
                                                    >
                                                        {DICE_COUNT_OPTIONS.map(count => (
                                                            <option key={count} value={count}>{count}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="text-xs text-gray-500 block mb-1">Sides</label>
                                                    <select
                                                        value={diceSides}
                                                        onChange={(e) => setDiceSides(parseInt(e.target.value))}
                                                        className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-badger-purple"
                                                    >
                                                        {DICE_SIDES_OPTIONS.map(sides => (
                                                            <option key={sides} value={sides}>d{sides}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="text-xs text-gray-500 block mb-1">Speed</label>
                                                    <select
                                                        value={animationSpeed}
                                                        onChange={(e) => setAnimationSpeed(e.target.value)}
                                                        className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-badger-purple"
                                                    >
                                                        {['fast', 'normal', 'slow'].map(speed => (
                                                            <option key={speed} value={speed}>{speed}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-2">
                                                {diceCount}d{diceSides} (range: {diceCount}-{diceCount * diceSides})
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Board Editor */}
                                <BoardEditor
                                    snakes={boardConfig.snakes}
                                    ladders={boardConfig.ladders}
                                    rows={boardSize.rows}
                                    cols={boardSize.cols}
                                    onConfigChange={handleConfigChange}
                                    onReset={handleResetConfig}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === "single" && (
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Single Simulation</h3>
                        <p className="text-gray-600 text-sm mb-4">
                            Run a single game simulation with detailed move-by-move analysis.
                        </p>

                        <SimulationConfig
                            boardSize={boardSize}
                            onBoardSizeChange={handleBoardSizeChange}
                            startPosition={startPosition}
                            onStartPositionChange={setStartPosition}
                            maxPosition={maxPosition}
                            diceCount={diceCount}
                            onDiceCountChange={setDiceCount}
                            diceSides={diceSides}
                            onDiceSidesChange={setDiceSides}
                        />

                        <button
                            onClick={handleSingleSimulation}
                            disabled={isSimulating}
                            className={`px-6 py-2 rounded-lg font-medium transition-colors mb-6 ${
                                isSimulating
                                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    : 'bg-badger-purple text-white hover:bg-purple-700'
                            }`}
                        >
                            {isSimulating ? 'Simulating...' : 'Simulate Game'}
                        </button>

                        {singleSimResult && (
                            <SingleGameResults result={singleSimResult} />
                        )}
                    </div>
                )}

                {activeTab === "multi" && (
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Multi-Simulation</h3>
                        <p className="text-gray-600 text-sm mb-4">
                            Run multiple simulations to analyse aggregate statistics and patterns.
                        </p>

                        <SimulationConfig
                            boardSize={boardSize}
                            onBoardSizeChange={handleBoardSizeChange}
                            startPosition={startPosition}
                            onStartPositionChange={setStartPosition}
                            maxPosition={maxPosition}
                            diceCount={diceCount}
                            onDiceCountChange={setDiceCount}
                            diceSides={diceSides}
                            onDiceSidesChange={setDiceSides}
                        />

                        <div className="flex gap-2 mb-6">
                            {[10, 100, 1000].map(count => (
                                <button
                                    key={count}
                                    onClick={() => handleMultiSimulation(count)}
                                    disabled={isSimulating}
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                        isSimulating
                                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            : 'bg-badger-purple text-white hover:bg-purple-700'
                                    }`}
                                >
                                    {isSimulating ? '...' : `${count} Games`}
                                </button>
                            ))}
                        </div>

                        {multiSimResult && (
                            <MultiGameResults result={multiSimResult} />
                        )}
                    </div>
                )}
            </div>
        </PageBody>
    );
};
